// eslint-disable-next-line static-service/require-shared-package-imports-only -- temporary
import "~/guide/ui/messages/message-bubble.css";

import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import moment from "moment";
import * as React from "react";

import {addStyle, View} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import Link from "@khanacademy/wonder-blocks-link";
import {Popover, PopoverContent} from "@khanacademy/wonder-blocks-popover";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    Body,
    Footnote,
    HeadingSmall,
    LabelLarge,
    LabelSmall,
} from "@khanacademy/wonder-blocks-typography";
import {
    bigqueryTraceURL,
    langfuseSessionURL,
    langfuseTraceURL,
} from "@repo/guide-core";
import {doNotTranslate} from "@repo/i18n";

import useProfile from "~/data-access/use-profile.ts";
// eslint-disable-next-line static-service/require-shared-package-imports-only -- temporary
import {FormattedText} from "~/guide/ui/formatted-text.tsx";
// eslint-disable-next-line static-service/require-shared-package-imports-only -- temporary
import {GuideColors} from "~/guide/ui/guide-colors.ts";
// eslint-disable-next-line static-service/require-shared-package-imports-only -- temporary
import {FlaggedAnswerText} from "~/guide/ui/messages/flagged-answer-text.tsx";

import type {
    guideTranscript_aiGuideThread_coeditingDocument_snapshots as CoeditingSnapshot,
    guideTranscript_aiGuideThread_coeditingDocument_snapshots_text as CoeditorState,
    guideTranscript_aiGuideThread_insights as Insights,
    guideTranscript_aiGuideThread_interactions as Interaction,
    guideTranscript_aiGuideThread as Thread,
} from "./__graphql-types__/guideTranscript.ts";
import {CoeditorSnapshot} from "./coeditor-snapshot.tsx";
import MaybeEssayReportLink from "./maybe-essay-report-link.tsx";
import MaybeResumeLink from "./maybe-resume-link.tsx";
import {combineInteractionsAndSnapshots} from "./utils.ts";

import imageIcon from "@phosphor-icons/core/regular/image.svg";
import infoIcon from "@phosphor-icons/core/regular/info.svg";
import warningIcon from "@phosphor-icons/core/regular/warning.svg";

interface Props {
    thread: Thread;
}

interface messageProps {
    type: "incoming" | "outgoing";
    content: string;
    flagged: boolean;
    userInputImage?: boolean;
}

interface messagesProps {
    interactions: Array<Interaction>;
    coeditingSnapshots: Array<CoeditingSnapshot> | null;
}

interface insightsProps {
    insights: Insights;
}

interface answerProps {
    answer: string;
    coeditorState?: CoeditorState;
    flagged: boolean;
    hasAccessToAIGuideDev: boolean;
    interactionId: string;
    interactionTraceId: string | null | undefined;
    stopped: boolean;
    userInputImage?: boolean;
}

interface questionProps {
    question: string;
    coeditorState?: CoeditorState;
    flagged: boolean;
}

interface imageProps {
    imageSummary: Nullable<string>;
    coeditorState?: CoeditorState;
    flagged: boolean;
}

interface imageIconPopoverProps {
    content: string;
}

const StyledDl = addStyle("dl");
const StyledDt = addStyle("dt");
const StyledDd = addStyle("dd");

const ImageIconPopover = ({content}: imageIconPopoverProps) => (
    <Popover
        placement="left"
        dismissEnabled
        content={
            <PopoverContent
                closeButtonVisible
                content={content}
                title={t`Image Placeholder`}
            />
        }
    >
        {({open}) => (
            <IconButton
                testId="image-placeholder"
                aria-label={t`Image Placeholder`}
                kind="tertiary"
                icon={infoIcon}
                onClick={open}
                style={{alignSelf: "center"}}
            />
        )}
    </Popover>
);

export const ModerationWarningIconWithPopover = ({
    popoverContent = t`This interaction with Khanmigo has been flagged by Khan Academy's moderation system.`,
}) => (
    <Popover
        placement="left"
        dismissEnabled
        content={
            <PopoverContent
                closeButtonVisible
                content={popoverContent}
                title={t`Moderation Warning`}
                actions={
                    <Link href="/r/khanmigo-moderation">{t`More Info`}</Link>
                }
            />
        }
    >
        {({open}) => (
            <IconButton
                aria-label={t`Moderation Warning`}
                icon={warningIcon}
                actionType="destructive"
                kind="tertiary"
                onClick={open}
                style={{alignSelf: "center"}}
            />
        )}
    </Popover>
);

const Message = ({
    type,
    content,
    flagged,
    userInputImage,
}: messageProps): React.ReactElement => {
    const isOutgoing = type === "outgoing";

    // We don't want to show flagged responses from the AI, to the user.
    const trimmedContent =
        flagged && !isOutgoing ? (
            <FlaggedAnswerText hasImage={userInputImage} />
        ) : (
            <FormattedText text={content.trim()} />
        );
    return (
        <div className={isOutgoing ? "ai-guide-sent-message" : ""}>
            <Body
                style={[
                    styles.messageBubble,
                    isOutgoing ? styles.userMessage : styles.botMessage,
                ]}
                testId={isOutgoing ? "sent-message" : "ai-message"}
            >
                {trimmedContent}
            </Body>
        </div>
    );
};

const Question = ({
    question,
    coeditorState,
    flagged,
}: questionProps): React.ReactElement | null => {
    if (!question) {
        return null;
    }

    const outgoingMessage = (
        <View style={styles.row}>
            {flagged && <ModerationWarningIconWithPopover />}
            <StyledDt style={styles.messageBubbleWrapper}>
                <Message type="outgoing" content={question} flagged={flagged} />
            </StyledDt>
        </View>
    );

    if (coeditorState) {
        return (
            <CoeditorSnapshot coeditorState={coeditorState} alignment="end">
                {outgoingMessage}
            </CoeditorSnapshot>
        );
    }

    return outgoingMessage;
};

const Answer = ({
    answer,
    coeditorState,
    flagged,
    hasAccessToAIGuideDev,
    interactionId,
    interactionTraceId,
    stopped,
    userInputImage,
}: answerProps): React.ReactElement | null => {
    if (!answer && !flagged) {
        return null;
    }
    const incomingMessage = (
        <StyledDd style={styles.messageBubbleWrapper}>
            <Message
                type="incoming"
                content={answer}
                flagged={flagged}
                userInputImage={userInputImage}
            />

            {(hasAccessToAIGuideDev || stopped) && (
                <View style={styles.messageFootnote}>
                    {hasAccessToAIGuideDev && (
                        <View style={styles.devLinks}>
                            <Link
                                href={`/api/internal/_ai-guide/interaction-completion/${interactionId}`}
                                target="_blank"
                            >
                                {doNotTranslate`View completion`}
                            </Link>
                            <Link
                                href={`/api/internal/_ai-guide/interaction-completion-replay/${interactionId}`}
                                target="_blank"
                            >
                                {doNotTranslate`Edit and replay`}
                            </Link>
                            <Link
                                href={`/api/internal/_ai-guide/interaction-completion-near/${interactionId}`}
                                target="_blank"
                            >
                                {doNotTranslate`View (maybe) related`}
                            </Link>
                            {interactionTraceId && (
                                <Link
                                    target="_blank"
                                    href={langfuseTraceURL(interactionTraceId)}
                                >
                                    {t`View Trace in Langfuse`}
                                </Link>
                            )}
                            {interactionId && (
                                <Link
                                    target="_blank"
                                    href={bigqueryTraceURL(interactionId)}
                                >
                                    {t`View Trace in BigQuery`}
                                </Link>
                            )}
                        </View>
                    )}

                    {stopped && (
                        <LabelSmall style={styles.responseWasStoppedByUser}>
                            {t`User stopped response`}
                        </LabelSmall>
                    )}
                </View>
            )}
        </StyledDd>
    );

    if (coeditorState) {
        return (
            <CoeditorSnapshot coeditorState={coeditorState} alignment="start">
                {incomingMessage}
            </CoeditorSnapshot>
        );
    }

    return incomingMessage;
};

const Image = ({
    imageSummary,
    coeditorState,
    flagged,
}: imageProps): React.ReactElement | null => {
    const imageTooltipContent = imageSummary
        ? t`We do not store any images that are sent to Khanmigo. An AI generated description of the image is shown here instead for your reference.`
        : t`We do not store any images that are sent to Khanmigo. This is a placeholder of where in the conversation the image was sent.`;

    const moderationPopoverContent = t`This image has been flagged by Khan Academy's moderation system. We do not store any images that are sent to Khanmigo.`;

    const outgoingMessage = (
        <View style={styles.image}>
            <View style={styles.imageIconAndInfoIcon}>
                {flagged ? (
                    <ModerationWarningIconWithPopover
                        popoverContent={moderationPopoverContent}
                    />
                ) : (
                    <ImageIconPopover content={imageTooltipContent} />
                )}
                <View style={styles.imageIcon}>
                    <PhosphorIcon
                        icon={imageIcon}
                        size="large"
                        color={color.offBlack64}
                    />
                </View>
            </View>
            <View style={styles.imageSummary}>
                <LabelSmall>{imageSummary}</LabelSmall>
            </View>
        </View>
    );

    if (coeditorState) {
        return (
            <CoeditorSnapshot coeditorState={coeditorState} alignment="end">
                {outgoingMessage}
            </CoeditorSnapshot>
        );
    }

    return outgoingMessage;
};

const Messages = (props: messagesProps): React.ReactElement => {
    const profileResult = useProfile();
    const hasAccessToAIGuideDev =
        profileResult?.profile?.get("hasAccessToAIGuideDev") ?? false;
    const interactionsWithCoeditorState = combineInteractionsAndSnapshots(
        props.interactions,
        props.coeditingSnapshots,
    );

    if (interactionsWithCoeditorState.length === 0) {
        return (
            <View style={{margin: "auto"}}>
                <Body style={styles.emptyInteractionBody}>
                    {t`This conversation is empty.`}
                </Body>
            </View>
        );
    }

    return (
        <StyledDl style={styles.messages}>
            {interactionsWithCoeditorState.map((interaction, index) => (
                <View key={index}>
                    <View style={{margin: "auto"}}>
                        <Footnote style={styles.messageTimestamp}>
                            {moment(interaction.createdAt).format("l LT")}
                        </Footnote>
                    </View>
                    {(interaction.question || interaction.answer) && (
                        <>
                            {interaction.userInputImage && (
                                <Image
                                    imageSummary={
                                        interaction.imageMetadata?.llmSummary
                                    }
                                    coeditorState={
                                        interaction.questionCoeditorState
                                    }
                                    flagged={interaction.flagged}
                                />
                            )}
                            <Question
                                question={interaction.question}
                                coeditorState={
                                    interaction.questionCoeditorState
                                }
                                flagged={interaction.flagged}
                            />
                            <Answer
                                answer={interaction.answer}
                                coeditorState={interaction.answerCoeditorState}
                                flagged={interaction.flagged}
                                userInputImage={Boolean(
                                    interaction.userInputImage,
                                )}
                                hasAccessToAIGuideDev={hasAccessToAIGuideDev}
                                interactionId={interaction.id}
                                interactionTraceId={interaction.traceID}
                                stopped={interaction.responseWasStoppedByUser}
                            />
                        </>
                    )}
                </View>
            ))}
        </StyledDl>
    );
};

const ThreadInsights = ({insights}: insightsProps): React.ReactElement => (
    <View>
        <HeadingSmall>{doNotTranslate`Insights`}</HeadingSmall>
        <LabelSmall>
            {doNotTranslate`The following are insights collected from this conversation.`}
        </LabelSmall>
        <Strut size={spacing.small_12} />
        <ul style={{listStyle: "inside"}}>
            <li>{`Title: ${insights.title || ""}`}</li>
            <li>{`Summary: ${insights.summary || ""}`}</li>
            <li>{`Interests: ${insights.interests || ""}`}</li>
            <li>{`Topics: ${insights.topics || ""}`}</li>
            <li>{`Off Topic: ${insights.offTopic || ""}`}</li>
            <li>{`Safety: ${insights.safety || ""}`}</li>
            <li>{`Languages: ${insights.languages || ""}`}</li>
            <li>{`Math: ${insights.math || ""}`}</li>
        </ul>
        <Strut size={spacing.small_12} />
    </View>
);

export default function ConversationView({thread}: Props): React.ReactElement {
    const profileResult = useProfile();
    const hasAccessToAIGuideDev = profileResult?.profile?.get(
        "hasAccessToAIGuideDev",
    );
    return (
        <View>
            <MaybeResumeLink
                threadOwnerKaid={thread.kaid}
                threadId={thread.id}
                interactions={thread.interactions}
            />
            <MaybeEssayReportLink thread={thread} />
            {hasAccessToAIGuideDev &&
                anyThreadInteractionsHaveTraceIds(thread) && (
                    <Link target="_blank" href={langfuseSessionURL(thread.id)}>
                        {t`View thread traces in Langfuse`}
                    </Link>
                )}
            {thread.summary && (
                <View>
                    <Strut size={spacing.xxLarge_48} />
                    <LabelLarge>{t`Summary`}:</LabelLarge>
                    <Strut size={spacing.xSmall_8} />
                    <Body>{thread.summary}</Body>
                </View>
            )}
            <Strut size={spacing.xLarge_32} />
            <View style={styles.separator} />
            <Strut size={spacing.xLarge_32} />
            <Messages
                interactions={thread.interactions}
                coeditingSnapshots={thread.coeditingDocument?.snapshots ?? null}
            />
            {hasAccessToAIGuideDev && thread.insights && (
                <>
                    <Strut size={spacing.small_12} />
                    <ThreadInsights insights={thread.insights} />
                </>
            )}
        </View>
    );
}

const anyThreadInteractionsHaveTraceIds = (thread: Thread) => {
    // if any interaction has a traceID, return true.
    for (const interaction of thread.interactions) {
        if (interaction.traceID) {
            return true;
        }
    }
    return false;
};

const messageBubblePadding = 15;
const botMessageMargin = spacing.small_12;

const styles = StyleSheet.create({
    image: {
        justifyContent: "flex-end",
        gap: spacing.xSmall_8,
        marginInlineEnd: spacing.small_12,
        marginBlockEnd: botMessageMargin,
    },
    imageIconAndInfoIcon: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "flex-end",
        width: "100%",
    },
    imageIcon: {
        padding: spacing.large_24,
        borderRadius: spacing.xSmall_8,
        backgroundColor: color.offBlack8,
        borderColor: color.offBlack64,
        borderWidth: 1,
    },
    imageSummary: {
        textAlign: "end",
        maxWidth: "50%",
        marginInlineStart: "auto",
    },

    // This is all imported form chatbot.jsx
    messages: {
        width: "100%",
        display: "flex",
        flexDirection: "column",
        listStyle: "none",
        background: color.white,
        borderRadius: 8,
        color: color.offBlack,
        marginBlockEnd: spacing.large_24,
    },
    messageBubbleWrapper: {
        display: "flex",
        flexDirection: "column",
        marginBlockStart: spacing.xSmall_8,
        marginBlockEnd: spacing.xSmall_8,
        maxWidth: "90%",
        width: "fit-content",
    },
    messageBubble: {
        flexDirection: "row",
        borderRadius: 8,
        padding: messageBubblePadding,
    },
    userMessage: {
        backgroundColor: GuideColors.darkPurple,
        color: color.white,
        marginInlineEnd: spacing.small_12,
        alignSelf: "flex-end",
    },
    botMessage: {
        alignSelf: "flex-start",
        backgroundColor: GuideColors.peach,
        marginInlineStart: spacing.small_12,
    },
    messageFootnote: {
        flexDirection: "row",
        justifyContent: "space-between",
        marginBlockStart: spacing.xSmall_8,
        marginInlineStart: botMessageMargin + messageBubblePadding,
    },
    responseWasStoppedByUser: {
        color: color.offBlack64,
    },
    row: {
        alignItems: "flex-end",
        justifyContent: "flex-end",
        flexDirection: "row",
        width: "100%",
    },
    messageTimestamp: {
        color: color.offBlack50,
        padding: spacing.xSmall_8,
    },
    emptyInteractionBody: {
        padding: 50,
    },
    separator: {
        height: 1,
        backgroundColor: color.offBlack16,
    },
    devLinks: {
        flexDirection: "row",
        gap: spacing.small_12,
    },
});
